import { CDN_BASE_URL } from '../api/constant';

/** Same-origin asset in `public/` — neutral “pending upload” graphic (not CRA `logo192` / React mark). */
export const ADMIN_MEDIA_PLACEHOLDER_PATH = '/admin-pending-media.svg';

export function getAdminMediaPlaceholderUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${ADMIN_MEDIA_PLACEHOLDER_PATH}`;
  }
  const base = String(CDN_BASE_URL || '').replace(/\/$/, '');
  return base ? `${base}${ADMIN_MEDIA_PLACEHOLDER_PATH}` : ADMIN_MEDIA_PLACEHOLDER_PATH;
}

/** True if this URL was used as a deferred-upload stand-in (same-origin SVG/PNG or legacy CRA logo). */
export function urlLooksLikeDeferredAdminPlaceholder(url) {
  if (typeof url !== 'string') return false;
  const u = url.trim();
  if (!u) return false;
  return (
    u.includes('/admin-pending-media.svg') ||
    u.includes('/logo192.png') ||
    u.includes('/logo512.png')
  );
}

/** Backend / form may still carry these names when media was never finalized. */
function looksLikePendingStorageToken(s) {
  if (typeof s !== 'string') return false;
  const t = s.trim();
  return t === 'pending-thumb' || t === 'pending-resource' || /^pending-/i.test(t);
}

/**
 * Whether the product payload from the API still references deferred placeholders anywhere.
 * Used to drop stale IndexedDB upload state after a successful pipeline (clear failed or navigate timing).
 *
 * NOTE: returns true the moment ANY single field is a placeholder. For deciding
 * storefront visibility — where a single stale placeholder shouldn't hide a
 * product that otherwise has real photos — use {@link productHasNoRealMedia}.
 */
export function productUsesDeferredMediaPlaceholders(product) {
  if (!product || typeof product !== 'object') return false;
  const thumb = product.thumbnail;
  if (typeof thumb === 'string') {
    if (urlLooksLikeDeferredAdminPlaceholder(thumb)) return true;
    if (looksLikePendingStorageToken(thumb)) return true;
  }
  const pr = product.productResources;
  if (Array.isArray(pr)) {
    for (const r of pr) {
      const u = r?.url;
      if (typeof u === 'string' && urlLooksLikeDeferredAdminPlaceholder(u)) return true;
      const n = r?.name;
      if (typeof n === 'string' && looksLikePendingStorageToken(n)) return true;
    }
  }
  const co = product.colorOptions;
  if (Array.isArray(co)) {
    for (const c of co) {
      const iu = c?.imageUrl;
      if (typeof iu === 'string' && urlLooksLikeDeferredAdminPlaceholder(iu)) return true;
    }
  }
  return false;
}

/**
 * First non-placeholder image URL anywhere on the product, in order of
 * preference: productResources → colorOptions → thumbnail. Returns null when
 * every candidate is empty / a placeholder. Lets the storefront fall back to
 * a real photo when the canonical thumbnail still points at a stale placeholder.
 */
export function firstRealImageUrl(product) {
  if (!product || typeof product !== 'object') return null;
  const isReal = (u) =>
    typeof u === 'string' && u.trim() !== '' && !urlLooksLikeDeferredAdminPlaceholder(u);

  const pr = product.productResources;
  if (Array.isArray(pr)) {
    for (const r of pr) {
      const u = r?.url;
      if (isReal(u)) return u;
    }
  }
  const co = product.colorOptions;
  if (Array.isArray(co)) {
    for (const c of co) {
      const u = c?.imageUrl;
      if (isReal(u)) return u;
    }
  }
  if (isReal(product?.thumbnail)) return product.thumbnail;
  return null;
}

/**
 * True iff the product has NO real image anywhere — every URL is either
 * missing or a deferred-upload placeholder. Storefront-visibility check uses
 * this so a product with at least one real photo is still shown (with that
 * photo as the card thumbnail), even when `product.thumbnail` itself happens
 * to be a stale placeholder.
 */
export function productHasNoRealMedia(product) {
  return firstRealImageUrl(product) === null;
}
